"use client";

import { useIsShopsite } from "@context/DomainContext";
import { XMarkIcon } from "@heroicons/react/24/solid";
import * as Toast from "@radix-ui/react-toast";
import { Button, Text } from "@components/theming";
import { useNotificationStore } from "@store/notificationStore";
import { BasketSummary } from "@types";
import { basketSummaryFetcher } from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import { useLatestCookieValue, useWindowSize } from "@utils/hooks";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import useSWR from "swr";

const Notification = () => {
  const notificationTranslations = useTranslations("Notification");
  const pathname = usePathname();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const windowSize = useWindowSize();
  const showNotification = useNotificationStore((state) => state.show);
  const setShowNotification = useNotificationStore((state) => state.setShow);
  const content = useNotificationStore((state) => state.content);
  const { data: basket } = useSWR<BasketSummary>(
    basketKey ? `basket/${basketKey}/summary` : null,
    basketSummaryFetcher,
    {
      refreshInterval: 60 * 1000,
      refreshWhenHidden: true,
    }
  );
  const isShopsite = useIsShopsite();

  const colorVariants: { [key: string]: string } = {
    emerald: "text-emerald-400",
    red: "text-red-400",
    orange: "text-orange-400",
  };

  if (
    !pathname.includes("cart") &&
    basket &&
    basket.Quantity > 0 &&
    windowSize.width &&
    windowSize.width <= 640
  ) {
    return (
      <Toast.Provider swipeDirection="down">
        <Toast.Root
          open={showNotification}
          onOpenChange={setShowNotification}
          type="foreground"
          className={joinClassNames(
            "flex w-full p-4 shadow-lg pointer-events-auto bg-[color:var(--gray-2)] backdrop-blur-popover ring-1 ring-black ring-opacity-5 overflow-hidden",
            "data-[swipe=move]:transition-none transition-all data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-bottom-full",
            isShopsite
              ? "bg-white rounded-lg"
              : "bg-[color:var(--gray-2)] rounded-[var(--radius-3)]"
          )}
          duration={Infinity}
        >
          <Toast.Description className="flex flex-col w-full">
            <div className="flex items-center justify-between">
              <div className="flex">
                {content.icon ? (
                  <content.icon.icon
                    aria-hidden="true"
                    className={joinClassNames(
                      `shrink-0 h-6 w-6 ${colorVariants[content.icon.color]}`
                    )}
                  />
                ) : null}
                <div className="ml-3 pt-0.5">
                  <Text
                    as="p"
                    className="text-sm font-medium"
                    shopsiteClasses="text-black"
                  >
                    {content?.content ===
                    "Zone limit reached! Can't add more than 1 ticket per zone."
                      ? notificationTranslations("zonelimitreached")
                      : content?.content}
                  </Text>
                </div>
              </div>
              <Toast.Close
                className={joinClassNames(
                  "inline-flex rounded-md h-min focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
                  isShopsite
                    ? "text-gray-400 hover:text-gray-500 focus-visible:ring-indigo-500"
                    : "text-[color:var(--gray-a11)] hover:text-[color:var(--gray-a9)] focus-visible:ring-[color:var(--accent-a9)]"
                )}
              >
                <span className="sr-only">
                  {notificationTranslations("close")}
                </span>
                <XMarkIcon className="w-5 h-5" aria-hidden="true" />
              </Toast.Close>
            </div>
            <div className="flex flex-col mt-6 gap-4">
              <Button
                href="/cart"
                onClick={() => setShowNotification(false)}
                sharedClasses="w-full"
                size="3"
              >
                {notificationTranslations("checkout")}
              </Button>
              {content?.extraContent}
              <button onClick={() => setShowNotification(false)}>
                {notificationTranslations("continueshopping")}
              </button>
            </div>
          </Toast.Description>
        </Toast.Root>
        <Toast.Viewport className="fixed bottom-4 w-full sm:top-[5.5rem] sm:right-0 flex flex-col sm:p-6 gap-3 sm:w-96 sm:max-w-[100vw] px-4 sm:m-0 z-[1000] outline-none" />
      </Toast.Provider>
    );
  }

  return (
    <Toast.Provider>
      <Toast.Root
        open={showNotification}
        onOpenChange={setShowNotification}
        type="foreground"
        className={joinClassNames(
          "flex w-full p-4 shadow-lg pointer-events-auto backdrop-blur-popover ring-1 ring-black ring-opacity-5 overflow-hidden",
          "data-[swipe=move]:transition-none transition-all data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:max-sm:slide-in-from-top-full data-[state=open]:sm:slide-in-from-right-full data-[state=closed]:sm:slide-out-to-right-full",
          isShopsite
            ? "bg-white rounded-lg"
            : "bg-[color:var(--gray-2)] rounded-[var(--radius-3)]"
        )}
        duration={
          pathname.includes("events") && basket && basket.Quantity > 0
            ? Infinity
            : 3000
        }
      >
        <Toast.Description className="flex flex-col w-full">
          <div className="flex justify-between gap-3">
            <div className="flex">
              {content.icon ? (
                <content.icon.icon
                  aria-hidden="true"
                  className={joinClassNames(
                    `shrink-0 h-6 w-6 ${colorVariants[content.icon.color]}`
                  )}
                />
              ) : null}
              <div className="ml-3 pt-0.5">
                <Text
                  as="p"
                  sharedClasses="text-sm font-medium"
                  shopsiteClasses="text-black"
                >
                  {content?.content}
                </Text>
              </div>
            </div>
            <Toast.Close
              className={joinClassNames(
                "inline-flex rounded-md h-min focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
                isShopsite
                  ? "text-gray-400 hover:text-gray-500 focus-visible:ring-indigo-500"
                  : "text-[color:var(--gray-a11)] hover:text-[color:var(--gray-a9)] focus-visible:ring-[color:var(--accent-a9)]"
              )}
            >
              <span className="sr-only">
                {notificationTranslations("close")}
              </span>
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </Toast.Close>
          </div>
          {!pathname.includes("cart") && basket && basket.Quantity > 0 ? (
            <div className="flex flex-col mt-6 gap-4">
              <Button
                href="/cart"
                onClick={() => setShowNotification(false)}
                sharedClasses="w-full"
                size="3"
              >
                {notificationTranslations("checkout")}
              </Button>
              {content?.extraContent}
              <button onClick={() => setShowNotification(false)}>
                {notificationTranslations("continueshopping")}
              </button>
            </div>
          ) : (
            <></>
          )}
        </Toast.Description>
      </Toast.Root>
      <Toast.Viewport className="fixed top-20 w-full sm:top-[5.5rem] sm:right-0 flex flex-col p-6 gap-3 sm:w-96 sm:max-w-[100vw] m-0 z-[1000] outline-none" />
    </Toast.Provider>
  );
};

export default Notification;
